/**
 * Buy-credits modal for Luna Intelligence.
 *
 * Two phases:
 *   1. Package picker — catalog fetched from paycentral, user clicks a
 *      card, chooses billing method (PIX default, card or boleto).
 *   2. Checkout view — after purchase() succeeds, we show the PIX QR /
 *      copy-and-paste code or the link Asaas gives us for card/boleto.
 *      The merchant completes payment in-place; once the Asaas webhook
 *      credits the balance server-side, the modal's "Atualizar saldo"
 *      button refreshes LunaPanel's balance chip.
 */

import { useEffect } from 'react';
import { useLuna } from '../../state/luna-store';

function formatBRL(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
}

function formatCredits(n: number): string {
  return new Intl.NumberFormat('pt-BR').format(n);
}

export function BuyCreditsModal() {
  const buyOpen = useLuna((s) => s.buyOpen);
  const close = useLuna((s) => s.closeBuyCredits);
  const packages = useLuna((s) => s.packages);
  const packagesLoading = useLuna((s) => s.packagesLoading);
  const purchaseError = useLuna((s) => s.purchaseError);
  const purchaseLoading = useLuna((s) => s.purchaseLoading);
  const checkout = useLuna((s) => s.checkout);
  const clearCheckout = useLuna((s) => s.clearCheckout);
  const startPurchase = useLuna((s) => s.startPurchase);
  const balance = useLuna((s) => s.balance);
  const refreshBalance = useLuna((s) => s.refreshBalance);

  // Poll balance while the user is paying — once the webhook credits
  // them, the chip will change and they'll see it without needing to
  // refresh the page manually.
  useEffect(() => {
    if (!buyOpen || !checkout) return;
    const id = setInterval(() => void refreshBalance(), 6_000);
    return () => clearInterval(id);
  }, [buyOpen, checkout, refreshBalance]);

  // ESC closes
  useEffect(() => {
    if (!buyOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [buyOpen, close]);

  if (!buyOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={close}
    >
      <div
        className="relative w-full max-w-[720px] overflow-hidden rounded-xl bg-surface shadow-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M6 6 4.5 4.5M19.5 19.5 18 18M6 18l-1.5 1.5M19.5 4.5 18 6" />
                </svg>
              </div>
              <h2 className="text-[15px] font-semibold text-text">Luna Intelligence — créditos</h2>
            </div>
            <p className="mt-1 text-[12px] text-text-subdued">
              {balance?.unlimited
                ? 'Modo desenvolvedor ativo — créditos ilimitados.'
                : `Saldo atual: ${balance ? formatCredits(balance.balance) : '…'} créditos.`}
            </p>
          </div>
          <button
            onClick={close}
            className="flex h-7 w-7 items-center justify-center rounded text-text-subdued hover:bg-surface-subdued hover:text-text"
            title="Fechar"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-5">
          {purchaseError && (
            <div className="mb-3 rounded border border-critical/30 bg-critical/5 px-3 py-2 text-[12px] text-critical">
              {purchaseError}
            </div>
          )}

          {checkout ? (
            <CheckoutView />
          ) : packagesLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-text-subdued">
              <span className="lc-spin h-4 w-4 rounded-full border-2 border-border border-t-interactive" />
              Carregando pacotes…
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {packages.map((p) => {
                const total = p.credits + (p.bonus_credits || 0);
                const pricePerK = p.price_cents / (total / 1000);
                return (
                  <div
                    key={p.slug}
                    className="flex flex-col gap-2 rounded-lg border border-border bg-surface p-4 transition-colors hover:border-interactive/40"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-[14px] font-semibold text-text">{p.name}</div>
                        <div className="mt-0.5 text-[12px] text-text-subdued">
                          {formatCredits(total)} créditos
                          {p.bonus_credits > 0 && (
                            <span className="ml-1 rounded bg-green-500/15 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                              +{formatCredits(p.bonus_credits)} bônus
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[18px] font-bold text-text">{formatBRL(p.price_cents)}</div>
                        <div className="text-[10px] text-text-subdued">
                          ≈ {formatBRL(pricePerK)}/1k
                        </div>
                      </div>
                    </div>

                    <div className="mt-1 flex gap-1.5">
                      <button
                        disabled={purchaseLoading}
                        onClick={() => void startPurchase(p.slug, 'PIX')}
                        className="flex-1 rounded bg-interactive px-3 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:opacity-50"
                      >
                        PIX
                      </button>
                      <button
                        disabled={purchaseLoading}
                        onClick={() => void startPurchase(p.slug, 'CREDIT_CARD')}
                        className="flex-1 rounded border border-border bg-surface px-3 py-1.5 text-[12px] font-medium text-text hover:bg-surface-subdued disabled:opacity-50"
                      >
                        Cartão
                      </button>
                      <button
                        disabled={purchaseLoading}
                        onClick={() => void startPurchase(p.slug, 'BOLETO')}
                        className="flex-1 rounded border border-border bg-surface px-3 py-1.5 text-[12px] font-medium text-text hover:bg-surface-subdued disabled:opacity-50"
                      >
                        Boleto
                      </button>
                    </div>
                  </div>
                );
              })}

              {packages.length === 0 && !packagesLoading && (
                <p className="col-span-2 py-10 text-center text-[13px] text-text-subdued">
                  Nenhum pacote disponível no momento.
                </p>
              )}
            </div>
          )}
        </div>

        {checkout && (
          <footer className="flex items-center justify-between border-t border-border bg-surface-subdued px-5 py-3">
            <button
              onClick={clearCheckout}
              className="text-[12px] font-medium text-interactive hover:underline"
            >
              ← Escolher outro pacote
            </button>
            <button
              onClick={() => void refreshBalance()}
              className="rounded border border-border bg-surface px-3 py-1.5 text-[12px] font-medium text-text hover:bg-surface-hovered"
            >
              Atualizar saldo
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

function CheckoutView() {
  const checkout = useLuna((s) => s.checkout!);
  const pixCopy = checkout.pix_copy_paste;
  const qr = checkout.qr_code_base64;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-center">
        <div className="text-[13px] text-text-subdued">Você comprou</div>
        <div className="text-[22px] font-bold text-text">
          {new Intl.NumberFormat('pt-BR').format(checkout.credits)} créditos
        </div>
        <div className="text-[13px] text-text-subdued">
          por {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
            checkout.amount_cents / 100
          )}
        </div>
      </div>

      {qr && (
        <div className="rounded-lg border border-border bg-white p-3">
          <img
            src={`data:image/png;base64,${qr}`}
            alt="QR Code PIX"
            className="h-[240px] w-[240px]"
          />
        </div>
      )}

      {pixCopy && (
        <div className="w-full">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-subdued">
            PIX copia e cola
          </label>
          <div className="flex gap-2">
            <input
              readOnly
              value={pixCopy}
              className="lc-input flex-1 font-mono text-[11px]"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              onClick={() => void navigator.clipboard.writeText(pixCopy)}
              className="rounded bg-interactive px-3 py-1.5 text-[12px] font-medium text-white hover:brightness-110"
            >
              Copiar
            </button>
          </div>
        </div>
      )}

      {checkout.payment_link && !qr && (
        <a
          href={checkout.payment_link}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded bg-interactive px-5 py-2.5 text-[13px] font-semibold text-white hover:brightness-110"
        >
          Abrir pagamento em nova aba →
        </a>
      )}

      <p className="max-w-[440px] text-center text-[12px] text-text-subdued">
        Assim que o pagamento for confirmado, seus créditos aparecem aqui automaticamente (pode levar alguns segundos após a compensação).
      </p>
    </div>
  );
}
